<template>
  <div class="callback-page">
    <div class="callback-card" :class="{ 'animate-in': pageLoaded }">
      <!-- 状态图标 -->
      <div class="status-icon" :class="statusClass">
        <svg
          v-if="status === 'loading'"
          class="spin-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M21 12a9 9 0 11-6.219-8.56" />
        </svg>
        <svg
          v-else-if="status === 'success'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        <svg
          v-else-if="status === 'error'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      </div>

      <!-- 状态标题 -->
      <h1 class="status-title">{{ statusTitle }}</h1>
      <p class="status-desc">{{ statusDesc }}</p>

      <!-- 用户信息卡片 -->
      <div v-if="userInfo" class="user-card">
        <div class="user-avatar">
          <img :src="userInfo.avatar" alt="头像" v-if="userInfo.avatar" />
          <div class="avatar-placeholder" v-else>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
        </div>
        <div class="user-info">
          <p class="user-name">
            {{ userInfo.nickname || "微信用户" }}
          </p>
          <p class="user-tag">登录成功</p>
        </div>
      </div>

      <!-- 真实姓名输入框 -->
      <div
        v-if="status === 'success' && !userInfo?.username"
        class="real-name-section"
      >
        <label class="input-label">真实姓名</label>
        <div class="input-wrapper">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            class="input-icon"
          >
            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <input
            v-model="realName"
            type="text"
            class="real-name-input"
            placeholder="请输入您的真实姓名"
            @keyup.enter="confirmLogin"
          />
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="action-buttons">
        <button
          v-if="status === 'success' && !userInfo?.username"
          class="primary-btn"
          :disabled="!realName.trim()"
          :class="{ disabled: !realName.trim() }"
          @click="confirmLogin"
        >
          <svg
            v-if="!isSubmitting"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          <svg
            v-else
            class="spin-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M21 12a9 9 0 11-6.219-8.56" />
          </svg>
          {{ isSubmitting ? "提交中..." : "确认登录" }}
        </button>
        <button
          v-if="status === 'error'"
          class="primary-btn"
          @click="retryLogin"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <polyline points="1 4 1 10 7 10" />
            <path d="M3.51 15a9 9 0 102.13-9.36L1 10" />
          </svg>
          重新登录
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getLoginInfo, updateUserInfo } from "@/api/api";

export default {
  name: "WeixinCallback",
  setup() {
    const route = useRoute();
    const router = useRouter();

    const status = ref("loading");
    const statusTitle = ref("正在登录...");
    const statusDesc = ref("微信授权中，请稍候");
    const userInfo = ref(null);
    const pageLoaded = ref(false);
    const realName = ref("");
    const isSubmitting = ref(false);
    const statusClass = ref("loading-icon");

    /**
     * ✅ 核心：安全跳转函数
     */
    const redirectToTarget = () => {
      const classroomid = route.query.classroomid;
      console.log("classroomid", classroomid);
      let target = "/";

      if (classroomid) {
        try {
          const decodedUrl = decodeURIComponent("/?classroomid=" + classroomid);

          // ✅ 只允许站内跳转，防止开放重定向攻击
          if (decodedUrl.startsWith("/")) {
            target = decodedUrl;
          }
        } catch (e) {
          console.warn("URL decode failed:", e);
        }
      }

      // ✅ 清掉地址栏 token / code（不刷新页面）
      window.history.replaceState(null, "", window.location.pathname);

      // ✅ 用 replace，不留历史记录
      router.replace(target);
    };

    /**
     * 处理微信回调
     */
    const handleWeixinCallback = async () => {
      const accesstoken = route.query.accesstoken;
      if (!accesstoken) {
        status.value = "error";
        statusClass.value = "error-icon";
        statusTitle.value = "登录失败";
        statusDesc.value = "未获取到微信授权信息，请重新登录";
        return;
      }

      try {
        const response = await getLoginInfo(accesstoken);

        if (response.data.code === 1) {
          const data = response.data.data;
          userInfo.value = data;

          // ✅ 统一存储登录态
          localStorage.setItem("token", data.accesstoken || "");
          localStorage.setItem("user_id", data.user_id || "");
          localStorage.setItem("userInfo", JSON.stringify(data));

          status.value = "success";
          statusClass.value = "success-icon";
          statusTitle.value = "登录成功";
          statusDesc.value = "欢迎使用签到系统";

          // ✅ 已绑定真实姓名，直接跳转
          if (data.username && data.username.trim()) {
            setTimeout(redirectToTarget, 800);
          }
        } else {
          throw new Error(response.data?.message || "登录失败");
        }
      } catch (error) {
        console.error("微信登录失败:", error);
        status.value = "error";
        statusClass.value = "error-icon";
        statusTitle.value = "登录失败";
        statusDesc.value = error.message || "微信授权失败，请稍后重试";
      }
    };

    const retryLogin = () => {
      localStorage.clear();
      window.location.reload();
    };

    /**
     * 提交真实姓名
     */
    const confirmLogin = async () => {
      if (!realName.value.trim() || isSubmitting.value) return;

      isSubmitting.value = true;

      try {
        const response = await updateUserInfo({
          real_name: realName.value.trim(),
        });

        if (response.data.code === 1) {
          const updatedUserInfo = {
            ...userInfo.value,
            username: realName.value.trim(),
            real_name: realName.value.trim(),
          };

          localStorage.setItem("userInfo", JSON.stringify(updatedUserInfo));

          // ✅ 提交成功后跳转
          setTimeout(redirectToTarget, 800);
        } else {
          throw new Error(response.data?.message || "保存失败");
        }
      } catch (error) {
        console.error("保存真实姓名失败:", error);
        alert(error.message || "保存失败，请重试");
      } finally {
        isSubmitting.value = false;
      }
    };

    onMounted(() => {
      setTimeout(() => {
        pageLoaded.value = true;
      }, 100);
      handleWeixinCallback();
    });

    return {
      status,
      statusTitle,
      statusDesc,
      statusClass,
      userInfo,
      pageLoaded,
      realName,
      isSubmitting,
      retryLogin,
      confirmLogin,
    };
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Noto+Serif+SC:wght@400;600;700&family=Noto+Sans+SC:wght@300;400;500;700&display=swap");

.callback-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: linear-gradient(135deg, #e8f4fd 0%, #d6ebfa 50%, #c5e3f8 100%);
  position: relative;
  overflow: hidden;
}

/* Floating bubbles background */
.callback-page::before,
.callback-page::after {
  content: "";
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.4) 0%,
    transparent 70%
  );
  pointer-events: none;
}

.callback-page::before {
  width: 600px;
  height: 600px;
  top: -200px;
  right: -200px;
  animation: float 20s ease-in-out infinite;
}

.callback-page::after {
  width: 400px;
  height: 400px;
  bottom: -100px;
  left: -100px;
  animation: float 25s ease-in-out infinite reverse;
}

@keyframes float {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -30px) scale(1.05);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.95);
  }
}

.callback-card {
  width: 100%;
  max-width: 400px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 32px;
  box-shadow: 0 20px 60px rgba(74, 144, 226, 0.15),
    0 4px 20px rgba(74, 144, 226, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.8);
  padding: 48px 32px;
  position: relative;
  overflow: hidden;
  opacity: 0;
  transform: translateY(30px);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  text-align: center;
}

.callback-card.animate-in {
  opacity: 1;
  transform: translateY(0);
  transition: all 0.8s cubic-bezier(0.22, 1, 0.36, 1);
}

/* Status Icon */
.status-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.status-icon svg {
  width: 40px;
  height: 40px;
}

.loading-icon {
  background: linear-gradient(135deg, #4a90e2 0%, #5ac8fa 100%);
  color: white;
  box-shadow: 0 8px 24px rgba(74, 144, 226, 0.3);
}

.success-icon {
  background: linear-gradient(135deg, #4a90e2 0%, #5ac8fa 100%);
  color: white;
  box-shadow: 0 8px 24px rgba(74, 144, 226, 0.3);
  animation: scaleIn 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}

.error-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%);
  color: white;
  box-shadow: 0 8px 24px rgba(255, 107, 107, 0.3);
  animation: scaleIn 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}

.spin-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

/* Status Text */
.status-title {
  font-family: "Noto Serif SC", serif;
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 8px;
  letter-spacing: 2px;
}

.status-desc {
  font-size: 14px;
  color: #8fa1b3;
  margin-bottom: 32px;
  line-height: 1.6;
}

/* User Card */
.user-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(
    135deg,
    rgba(74, 144, 226, 0.06) 0%,
    rgba(90, 200, 250, 0.06) 100%
  );
  border-radius: 16px;
  border: 1px solid rgba(74, 144, 226, 0.1);
  margin-bottom: 24px;
  text-align: left;
}

.user-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: linear-gradient(135deg, #4a90e2 0%, #5ac8fa 100%);
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.avatar-placeholder svg {
  width: 28px;
  height: 28px;
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.user-tag {
  font-size: 12px;
  color: #4a90e2;
  background: rgba(74, 144, 226, 0.1);
  padding: 2px 10px;
  border-radius: 10px;
  display: inline-block;
}

/* Real Name Section */
.real-name-section {
  margin-bottom: 24px;
  text-align: left;
}

.input-label {
  font-size: 14px;
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 10px;
  display: block;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 14px;
  width: 18px;
  height: 18px;
  color: #8fa1b3;
  pointer-events: none;
}

.real-name-input {
  width: 100%;
  padding: 14px 14px 14px 48px;
  font-size: 15px;
  color: #2c3e50;
  background: rgba(74, 144, 226, 0.04);
  border: 1px solid rgba(74, 144, 226, 0.2);
  border-radius: 14px;
  outline: none;
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  font-family: "Noto Sans SC", sans-serif;
}

.real-name-input::placeholder {
  color: #8fa1b3;
}

.real-name-input:focus {
  border-color: #4a90e2;
  background: rgba(74, 144, 226, 0.08);
  box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
}

/* Action Buttons */
.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.primary-btn {
  width: 100%;
  padding: 14px 24px;
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, #4a90e2 0%, #5ac8fa 100%);
  border: none;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  font-family: "Noto Sans SC", sans-serif;
  box-shadow: 0 4px 16px rgba(74, 144, 226, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.primary-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(74, 144, 226, 0.4);
}

.primary-btn:active {
  transform: scale(0.98);
}

.primary-btn svg {
  width: 18px;
  height: 18px;
}

.primary-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.primary-btn.disabled:hover {
  transform: none;
  box-shadow: 0 4px 16px rgba(74, 144, 226, 0.3);
}

/* Responsive */
@media (max-width: 480px) {
  .callback-card {
    padding: 36px 24px;
    border-radius: 24px;
  }

  .status-title {
    font-size: 20px;
  }

  .status-icon {
    width: 64px;
    height: 64px;
  }

  .status-icon svg {
    width: 32px;
    height: 32px;
  }

  .user-card {
    padding: 16px;
  }
}
</style>
